import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatLocalDate } from "@topzle/locale";
import type { DashboardRoomSummary, RoomSummary } from "@topzle/types";
import {
  ArrowRight,
  BarChart3,
  Bell,
  Bot,
  Bug,
  Check,
  Clock3,
  Gamepad2,
  LoaderCircle,
  LogIn,
  MessageSquareText,
  Plus,
  Search,
  Share2,
  Sparkles,
  Trash2,
  Trophy,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { trackEvent } from "../analytics";
import { api } from "../api";
import { LoadingButton } from "../components/LoadingButton";
import { isIdentityError, RecoverableError } from "../components/RecoverableError";
import { SupportDialog } from "../components/SupportDialog";
import type { SupportRequestKind } from "../components/SupportRequestForm";
import { TurnstileWidget, type TurnstileWidgetHandle } from "../components/TurnstileWidget";
import { gameLaunchIntent } from "../gameLaunchIntent";
import {
  funRoomName,
  roomGameNames as gameNames,
  type PlayableRoomGameId,
  roomGameCatalog,
  roomGameSymbols as starterGameSymbols,
} from "../roomGameCatalog";
import { type RoomSession, useAppStore } from "../state";
import { supabase } from "../supabase";
import { getUserAvatar, getUserDisplayName, useAuthSession } from "../useAuthSession";

interface RoomCreationOptions {
  title: string;
  starterGameId: PlayableRoomGameId;
  visibility: "public" | "private";
  inviteContact?: string;
}
interface DirectoryProfile {
  id: string;
  username: string | null;
  display_name: string;
  avatar_url: string | null;
  status?: string | null;
}
interface FriendRequestItem {
  id: string;
  sender_id: string;
  receiver_id: string;
  profile: DirectoryProfile | null;
}
interface FriendDashboardData {
  friends: Array<DirectoryProfile & { favorite: boolean }>;
  incoming: FriendRequestItem[];
  outgoing: FriendRequestItem[];
}
type DashboardSection = "home" | "friends" | "stats" | "notifications";
type RoomSort = "newest" | "updated";

const emptyFriendData: FriendDashboardData = { friends: [], incoming: [], outgoing: [] };
const restorableRoomStates = new Set(["waiting", "active", "paused", "reconnecting", "restoring"]);

function gameName(gameId: string | null) {
  return gameId && gameId in gameNames
    ? gameNames[gameId as RoomCreationOptions["starterGameId"]]
    : "Game Night";
}

function displayRoomTitle(title: string, gameId: string | null) {
  if (!gameId || !/('|’)s room$/i.test(title.trim())) return title;
  return title.replace(/room$/i, `${gameName(gameId)} Arena`);
}

export function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuthSession();
  const guest = useAppStore((state) => state.guest);
  const setGuest = useAppStore((state) => state.setGuest);
  const clearGuest = useAppStore((state) => state.clearGuest);
  const recentRooms = useAppStore((state) => state.recentRooms);
  const gameActivity = useAppStore((state) => state.gameActivity);
  const setRoom = useAppStore((state) => state.setRoom);
  const forgetRoom = useAppStore((state) => state.forgetRoom);
  const [section, setSection] = useState<DashboardSection>("home");
  const [roomSort, setRoomSort] = useState<RoomSort>("newest");
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorEntry, setCreatorEntry] = useState<"general" | "game">("general");
  const [title, setTitle] = useState("");
  const [titleCustomized, setTitleCustomized] = useState(false);
  const [starterGameId, setStarterGameId] =
    useState<RoomCreationOptions["starterGameId"]>("tic-tac-toe");
  const [roomVisibility, setRoomVisibility] = useState<RoomCreationOptions["visibility"] | null>(
    null,
  );
  const [inviteContact, setInviteContact] = useState("");
  const [sharingRoom, setSharingRoom] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [supportKind, setSupportKind] = useState<Exclude<SupportRequestKind, "support"> | null>(
    null,
  );
  const [friendSearch, setFriendSearch] = useState("");
  const [searchResults, setSearchResults] = useState<DirectoryProfile[]>([]);
  const [guestChallengeToken, setGuestChallengeToken] = useState<string | null>(null);
  const guestChallengeRef = useRef<TurnstileWidgetHandle>(null);
  const guestIdentityPromise = useRef<ReturnType<typeof api.createGuest> | null>(null);
  const matchmakingViewKey = useRef("");
  const pendingGameLaunch = useRef(gameLaunchIntent(window.location.search));

  const ensurePlayableIdentity = useCallback(async () => {
    if (session)
      return {
        id: session.user.id,
        token: "",
        displayName: getUserDisplayName(session.user),
        avatar: getUserAvatar(session.user),
        registered: true,
      };
    const existingGuest = useAppStore.getState().guest;
    if (existingGuest) return { ...existingGuest, registered: false };
    if (!guestChallengeToken)
      throw new Error("Complete the security check to continue as a guest.");
    guestIdentityPromise.current ??= api.createGuest(guestChallengeToken);
    try {
      const created = await guestIdentityPromise.current;
      setGuest(created);
      trackEvent("guest_play_started", { source: "dashboard" });
      return { ...created, registered: false };
    } finally {
      guestIdentityPromise.current = null;
    }
  }, [guestChallengeToken, session, setGuest]);

  const createRoom = useMutation({
    mutationFn: async (options: RoomCreationOptions) => {
      const identity = await ensurePlayableIdentity();
      const room = await api.createRoom({
        title: options.title.trim() || funRoomName(identity.displayName, options.starterGameId),
        starterGameId: options.starterGameId,
        visibility: options.visibility,
        joinPolicy: options.visibility === "public" ? "open" : "invite",
        aiPlayersEnabled: true,
        aiDifficulty: "medium",
        aiArrivalBehavior: "spectate_ai_game",
      });
      const joined = await api.joinRoom(room.id, {
        displayName: identity.displayName,
        avatar: identity.avatar,
        role: identity.registered ? "player" : "guest",
      });
      setRoom({
        roomId: room.id,
        participantId: joined.participantId,
        displayName: identity.displayName,
        avatar: identity.avatar,
        websocketUrl: joined.websocketUrl,
        reconnectToken: joined.reconnectToken,
        realtimeSignalToken: joined.realtimeSignalToken,
        snapshot: joined.room,
        lastVisitedAt: Date.now(),
      });
      let inviteNotice: string | undefined;
      const inviteContact = options.inviteContact?.trim();
      if (options.visibility === "private" && inviteContact) {
        if (inviteContact.includes("@")) {
          try {
            await api.sendEmailInvitation({ to: inviteContact, roomId: room.id });
            inviteNotice = `Invitation sent to ${inviteContact}.`;
          } catch {
            inviteNotice =
              "The room is ready, but the email invitation could not be sent. Use Share invite here instead.";
          }
        } else {
          inviteNotice =
            "Your private invite link is ready. Use Share invite to send it by phone, WhatsApp, or any other app.";
        }
      }
      return {
        roomId: room.id,
        openGamePicker: true,
        gameId: options.starterGameId,
        inviteNotice,
      };
    },
    onSuccess: (result, options) => {
      trackEvent("room_created", {
        game_id: options.starterGameId,
        visibility: options.visibility,
        identity_type: session ? "registered" : "guest",
      });
      setCreatorOpen(false);
      navigate(`/room/${result.roomId}`, {
        state: {
          openGamePicker: result.openGamePicker,
          gameId: result.gameId,
          inviteNotice: result.inviteNotice,
        },
      });
    },
    onError: (error) => {
      trackEvent("operation_failed", {
        operation: "room_create",
        game_id: starterGameId,
        route: "/app",
      });
      if (isIdentityError(error)) clearGuest();
      setGuestChallengeToken(null);
      guestChallengeRef.current?.reset();
    },
  });
  const publicRooms = useQuery({
    queryKey: ["public-waiting-rooms", starterGameId, session?.user.id ?? guest?.id ?? "guest"],
    queryFn: async () => {
      await ensurePlayableIdentity();
      return api.publicWaitingRooms(starterGameId, 4);
    },
    enabled:
      creatorOpen &&
      roomVisibility === "public" &&
      Boolean(session || guest || guestChallengeToken),
    refetchInterval: 5_000,
    retry: 1,
  });
  useEffect(() => {
    if (!creatorOpen || roomVisibility !== "public" || !publicRooms.data) return;
    const key = `${starterGameId}:${publicRooms.data.rooms.length}`;
    if (matchmakingViewKey.current === key) return;
    matchmakingViewKey.current = key;
    trackEvent("matchmaking_viewed", {
      game_id: starterGameId,
      waiting_room_count: publicRooms.data.rooms.length,
    });
  }, [creatorOpen, publicRooms.data, roomVisibility, starterGameId]);
  const joinPublicRoom = useMutation({
    mutationFn: async (room: RoomSummary) => {
      const identity = await ensurePlayableIdentity();
      const { displayName, avatar } = identity;
      const joined = await api.joinRoom(room.id, {
        displayName,
        avatar,
        role: identity.registered ? "player" : "guest",
      });
      setRoom({
        roomId: room.id,
        participantId: joined.participantId,
        displayName,
        avatar,
        websocketUrl: joined.websocketUrl,
        reconnectToken: joined.reconnectToken,
        realtimeSignalToken: joined.realtimeSignalToken,
        snapshot: joined.room,
        lastVisitedAt: Date.now(),
      });
      return { roomId: room.id, gameId: room.currentGameId ?? starterGameId };
    },
    onSuccess: (joined) => {
      trackEvent("matchmaking_joined", { game_id: joined.gameId });
      trackEvent("room_joined", {
        game_id: joined.gameId,
        identity_type: session ? "registered" : "guest",
        join_source: "matchmaking",
        role: "player",
      });
      setCreatorOpen(false);
      navigate(`/room/${joined.roomId}`);
    },
    onError: (error) => {
      trackEvent("operation_failed", {
        operation: "matchmaking_join",
        game_id: starterGameId,
        route: "/app",
      });
      if (isIdentityError(error)) clearGuest();
      setGuestChallengeToken(null);
      guestChallengeRef.current?.reset();
    },
  });
  const cloudDashboard = useQuery({
    queryKey: ["dashboard", session?.user.id],
    queryFn: () => api.dashboard(),
    enabled: Boolean(session),
    retry: 1,
  });
  const cloudRooms = (cloudDashboard.data?.rooms ?? []).filter(
    (saved) =>
      restorableRoomStates.has(saved.lifecycle) &&
      !recentRooms.some((local) => local.roomId === saved.id),
  );
  const roomHistory = [
    ...cloudRooms.map((room) => ({
      kind: "cloud" as const,
      room,
      createdAt: Date.parse(room.createdAt),
      updatedAt: Date.parse(room.updatedAt),
    })),
    ...recentRooms.map((room) => ({
      kind: "local" as const,
      room,
      createdAt: Math.min(
        room.lastVisitedAt,
        ...room.snapshot.participants.map((participant) => participant.joinedAt),
      ),
      updatedAt: Math.max(room.lastVisitedAt, room.snapshot.updatedAt),
    })),
  ].sort((left, right) =>
    roomSort === "updated"
      ? right.updatedAt - left.updatedAt || right.createdAt - left.createdAt
      : right.createdAt - left.createdAt || right.updatedAt - left.updatedAt,
  );
  const rejoinCloudRoom = useMutation({
    mutationFn: async (saved: DashboardRoomSummary) => {
      if (!session) throw new Error("Sign in to restore this room.");
      const displayName = getUserDisplayName(session.user);
      const avatar = getUserAvatar(session.user);
      const joined = await api.joinRoom(saved.id, {
        displayName,
        avatar,
        role: saved.role === "spectator" ? "spectator" : "player",
      });
      setRoom({
        roomId: saved.id,
        participantId: joined.participantId,
        displayName,
        avatar,
        websocketUrl: joined.websocketUrl,
        reconnectToken: joined.reconnectToken,
        realtimeSignalToken: joined.realtimeSignalToken,
        snapshot: joined.room,
        lastVisitedAt: Date.now(),
      });
      return { roomId: saved.id, gameId: saved.currentGameId ?? "waiting" };
    },
    onSuccess: (joined) => {
      trackEvent("room_rejoined", {
        game_id: joined.gameId,
        identity_type: "registered",
        source: "cloud_dashboard",
      });
      navigate(`/room/${joined.roomId}`);
    },
  });
  const removeCloudRoom = useMutation({
    mutationFn: (roomId: string) => api.removeDashboardRoom(roomId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const friends = useQuery({
    queryKey: ["dashboard-friends", session?.user.id],
    enabled: Boolean(session && supabase),
    queryFn: async (): Promise<FriendDashboardData> => {
      if (!session || !supabase) return emptyFriendData;
      const userId = session.user.id;
      const [friendshipsResult, incomingResult, outgoingResult] = await Promise.all([
        supabase
          .from("friendships")
          .select("friend_id,favorite")
          .eq("user_id", userId)
          .order("created_at", { ascending: false }),
        supabase
          .from("friend_requests")
          .select("id,sender_id,receiver_id")
          .eq("receiver_id", userId)
          .eq("status", "pending")
          .order("created_at", { ascending: false }),
        supabase
          .from("friend_requests")
          .select("id,sender_id,receiver_id")
          .eq("sender_id", userId)
          .eq("status", "pending")
          .order("created_at", { ascending: false }),
      ]);
      const error = friendshipsResult.error ?? incomingResult.error ?? outgoingResult.error;
      if (error) throw error;
      const friendshipRows = friendshipsResult.data ?? [];
      const incomingRows = incomingResult.data ?? [];
      const outgoingRows = outgoingResult.data ?? [];
      const ids = [
        ...friendshipRows.map((row) => row.friend_id),
        ...incomingRows.map((row) => row.sender_id),
        ...outgoingRows.map((row) => row.receiver_id),
      ];
      const uniqueIds = [...new Set(ids)];
      const directoryResult = uniqueIds.length
        ? await supabase
            .from("profile_directory")
            .select("id,username,display_name,avatar_url,status")
            .in("id", uniqueIds)
        : { data: [], error: null };
      if (directoryResult.error) throw directoryResult.error;
      const profiles = new Map(
        (directoryResult.data as DirectoryProfile[]).map((profile) => [profile.id, profile]),
      );
      return {
        friends: friendshipRows.flatMap((row) => {
          const profile = profiles.get(row.friend_id);
          return profile ? [{ ...profile, favorite: row.favorite }] : [];
        }),
        incoming: incomingRows.map((row) => ({
          ...row,
          profile: profiles.get(row.sender_id) ?? null,
        })),
        outgoing: outgoingRows.map((row) => ({
          ...row,
          profile: profiles.get(row.receiver_id) ?? null,
        })),
      };
    },
  });

  const searchFriends = useMutation({
    mutationFn: async () => {
      if (!session || !supabase) throw new Error("Sign in to find friends.");
      const term = friendSearch.trim().replace(/[,()%]/g, "");
      if (term.length < 2) throw new Error("Enter at least two characters.");
      const result = await supabase
        .from("profile_directory")
        .select("id,username,display_name,avatar_url,status")
        .neq("id", session.user.id)
        .or(`username.ilike.%${term}%,display_name.ilike.%${term}%`)
        .limit(12);
      if (result.error) throw result.error;
      return result.data as DirectoryProfile[];
    },
    onSuccess: setSearchResults,
  });
  const sendFriendRequest = useMutation({
    mutationFn: async (receiverId: string) => {
      if (!session || !supabase) throw new Error("Sign in to add friends.");
      const result = await supabase.from("friend_requests").insert({
        sender_id: session.user.id,
        receiver_id: receiverId,
      });
      if (result.error) throw result.error;
      return receiverId;
    },
    onSuccess: async (receiverId) => {
      setSearchResults((items) => items.filter((item) => item.id !== receiverId));
      await queryClient.invalidateQueries({ queryKey: ["dashboard-friends"] });
    },
  });
  const acceptFriendRequest = useMutation({
    mutationFn: async (requestId: string) => {
      if (!supabase) throw new Error("Friends are not configured.");
      const result = await supabase.rpc("accept_friend_request", { request_id: requestId });
      if (result.error) throw result.error;
    },
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["dashboard-friends"] }),
  });

  const stats = useMemo(() => {
    const counts = new Map<string, number>();
    for (const game of gameActivity) counts.set(game.gameId, (counts.get(game.gameId) ?? 0) + 1);
    const favorite = [...counts.entries()].sort((left, right) => right[1] - left[1])[0];
    return {
      rooms: recentRooms.length,
      games: gameActivity.length,
      aiGames: gameActivity.filter((game) => game.versusAi).length,
      wins: gameActivity.filter(
        (game) =>
          game.result?.status === "completed" && game.result.winnerIds.includes(game.playerId),
      ).length,
      favorite: favorite?.[0] ?? "No games yet",
    };
  }, [gameActivity, recentRooms.length]);

  const openCreator = () => {
    const name = session ? getUserDisplayName(session.user) : guest?.displayName;
    setTitle(funRoomName(name ?? "Player", starterGameId));
    setTitleCustomized(false);
    setRoomVisibility(null);
    setCreatorEntry("general");
    setInviteContact("");
    createRoom.reset();
    joinPublicRoom.reset();
    setCreatorOpen(true);
  };
  const openCreatorForGame = useCallback(
    (gameId: PlayableRoomGameId) => {
      const name = session ? getUserDisplayName(session.user) : guest?.displayName;
      setStarterGameId(gameId);
      setTitle(funRoomName(name ?? "Player", gameId));
      setTitleCustomized(false);
      setRoomVisibility("public");
      setCreatorEntry("game");
      setInviteContact("");
      createRoom.reset();
      joinPublicRoom.reset();
      setCreatorOpen(true);
    },
    [createRoom, guest?.displayName, joinPublicRoom, session],
  );
  useEffect(() => {
    const gameId = pendingGameLaunch.current;
    if (!gameId) return;
    pendingGameLaunch.current = null;
    openCreatorForGame(gameId);
    navigate("/app", { replace: true });
  }, [navigate, openCreatorForGame]);
  const submitRoom = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!roomVisibility) return;
    createRoom.mutate({
      title,
      starterGameId,
      visibility: roomVisibility,
      ...(roomVisibility === "private" && inviteContact.trim() ? { inviteContact } : {}),
    });
  };
  const rejoinRoom = (room: RoomSession) => {
    setRoom({ ...room, lastVisitedAt: Date.now() });
    navigate(`/room/${room.roomId}`);
  };
  const shareRoom = async (room: RoomSession) => {
    const url = `${window.location.origin}/join/${room.snapshot.code}`;
    setSharingRoom(room.roomId);
    setNotice(null);
    try {
      if (navigator.share)
        await navigator.share({
          title: room.snapshot.settings.title,
          text: "Join my Topzle room",
          url,
        });
      else {
        await navigator.clipboard.writeText(url);
        setNotice("Invite link copied.");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setNotice("Could not share automatically. Open the room and copy its six-letter code.");
    } finally {
      setSharingRoom(null);
    }
  };
  const displayName = session
    ? getUserDisplayName(session.user)
    : (guest?.displayName ?? "Guest player");

  return (
    <section className="app-shell">
      <aside className="app-sidebar">
        <div className="profile-chip">
          <span>{displayName.slice(0, 1).toUpperCase()}</span>
          <div>
            <strong>{displayName}</strong>
            <small>{session?.user.email ?? (guest ? "Playing as guest" : "No profile yet")}</small>
          </div>
        </div>
        <nav aria-label="Dashboard">
          <button
            className={section === "home" ? "active" : ""}
            type="button"
            onClick={() => setSection("home")}
          >
            <Gamepad2 /> Home
          </button>
          <button
            className={section === "friends" ? "active" : ""}
            type="button"
            onClick={() => setSection("friends")}
          >
            <Users /> Friends
            {friends.data?.incoming.length ? <b>{friends.data.incoming.length}</b> : null}
          </button>
          <button
            className={section === "stats" ? "active" : ""}
            type="button"
            onClick={() => setSection("stats")}
          >
            <BarChart3 /> Game stats
          </button>
          <button
            className={section === "notifications" ? "active" : ""}
            type="button"
            onClick={() => setSection("notifications")}
          >
            <Bell /> Invites
          </button>
        </nav>
      </aside>
      <div className="dashboard">
        <div className="dashboard-head">
          <div>
            <span className="eyebrow">
              {section === "home" ? "YOUR PLAYGROUND" : "YOUR TOPZLE"}
            </span>
            <h1>
              {section === "home"
                ? `Welcome back, ${displayName.split(" ")[0]}.`
                : section === "friends"
                  ? "Friends make the room."
                  : section === "stats"
                    ? "Your game story."
                    : "Invites and activity."}
            </h1>
          </div>
          <div className="dashboard-head-actions">
            <button type="button" className="button button-ghost" onClick={() => navigate("/join")}>
              Join
            </button>
            <LoadingButton
              type="button"
              className="button button-primary"
              onClick={openCreator}
              loading={createRoom.isPending}
              loadingLabel="Starting…"
            >
              <Plus /> Start a room
            </LoadingButton>
          </div>
        </div>
        {notice && <div className="status-message dashboard-notice">{notice}</div>}

        {section === "home" && (
          <>
            <section className="dashboard-game-rack">
              <div>
                <span className="eyebrow">PICK A GAME</span>
                <h2>Play now.</h2>
                <Link className="text-link" to="/how-to-play">
                  Game guides <ArrowRight />
                </Link>
              </div>
              <div className="dashboard-game-list">
                {roomGameCatalog.map((game, index) => (
                  <article className={`dashboard-game game-color-${index % 6}`} key={game.id}>
                    <button type="button" onClick={() => openCreatorForGame(game.id)}>
                      <b>{starterGameSymbols[game.id]}</b>
                      <span>{game.name}</span>
                      <ArrowRight />
                    </button>
                    <Link to={`/how-to-play/${game.id}`} aria-label={`How to play ${game.name}`}>
                      Guide
                    </Link>
                  </article>
                ))}
              </div>
            </section>
            <section className="dashboard-section">
              <div className="section-heading">
                <div>
                  <h2>Recent rooms</h2>
                  <span>Jump back in.</span>
                </div>
                <div className="room-history-actions">
                  <label>
                    <span className="sr-only">Sort recent rooms</span>
                    <select
                      value={roomSort}
                      onChange={(event) => setRoomSort(event.target.value as RoomSort)}
                      aria-label="Sort recent rooms"
                    >
                      <option value="newest">New rooms</option>
                      <option value="updated">Recently updated</option>
                    </select>
                  </label>
                  <button type="button" className="text-link" onClick={() => setSection("stats")}>
                    View stats <ArrowRight />
                  </button>
                </div>
              </div>
              {recentRooms.length || cloudRooms.length ? (
                <div className="room-history-grid">
                  {roomHistory.map((item) =>
                    item.kind === "cloud" ? (
                      <article className="room-history-card synced" key={item.room.id}>
                        <div className="room-history-top">
                          <span className={`room-state ${item.room.lifecycle}`} />{" "}
                          <b>{item.room.lifecycle}</b>
                          <time>
                            <Clock3 /> {formatLocalDate(item.room.updatedAt)}
                          </time>
                        </div>
                        <h3>{displayRoomTitle(item.room.title, item.room.currentGameId)}</h3>
                        <p>
                          <span>{item.room.code}</span> · {gameName(item.room.currentGameId)}
                        </p>
                        <div>
                          <LoadingButton
                            className="button button-primary compact"
                            type="button"
                            onClick={() => rejoinCloudRoom.mutate(item.room)}
                            loading={
                              rejoinCloudRoom.isPending &&
                              rejoinCloudRoom.variables?.id === item.room.id
                            }
                            loadingLabel="Restoring…"
                          >
                            <LogIn /> Rejoin
                          </LoadingButton>
                          <span className="synced-label">Synced to account</span>
                          <button
                            className="icon-button"
                            type="button"
                            aria-label={`Remove ${item.room.title} from your dashboard`}
                            onClick={() => removeCloudRoom.mutate(item.room.id)}
                            disabled={removeCloudRoom.isPending}
                          >
                            {removeCloudRoom.isPending &&
                            removeCloudRoom.variables === item.room.id ? (
                              <LoaderCircle className="spin" />
                            ) : (
                              <Trash2 />
                            )}
                          </button>
                        </div>
                      </article>
                    ) : (
                      <article className="room-history-card" key={item.room.roomId}>
                        <div className="room-history-top">
                          <span className={`room-state ${item.room.snapshot.lifecycle}`} />{" "}
                          <b>{item.room.snapshot.lifecycle}</b>
                          <time>
                            <Clock3 /> {formatLocalDate(item.room.snapshot.updatedAt)}
                          </time>
                        </div>
                        <h3>
                          {displayRoomTitle(
                            item.room.snapshot.settings.title,
                            item.room.snapshot.currentGameId,
                          )}
                        </h3>
                        <p>
                          <span>{item.room.snapshot.code}</span> ·{" "}
                          {item.room.snapshot.participants.length} people ·{" "}
                          {gameName(item.room.snapshot.currentGameId)}
                        </p>
                        <div>
                          <button
                            className="button button-primary compact"
                            type="button"
                            onClick={() => rejoinRoom(item.room)}
                          >
                            <LogIn /> Rejoin
                          </button>
                          <LoadingButton
                            className="button button-ghost compact"
                            type="button"
                            onClick={() => void shareRoom(item.room)}
                            loading={sharingRoom === item.room.roomId}
                            loadingLabel="Sharing…"
                          >
                            <Share2 /> Invite
                          </LoadingButton>
                          <button
                            className="icon-button"
                            type="button"
                            aria-label={`Forget ${item.room.snapshot.settings.title}`}
                            onClick={() => forgetRoom(item.room.roomId)}
                          >
                            <Trash2 />
                          </button>
                        </div>
                      </article>
                    ),
                  )}
                </div>
              ) : (
                <div className="empty-card">
                  <Gamepad2 />
                  <strong>No recent rooms yet.</strong>
                  <p>Start a game and it will show up here.</p>
                  <button type="button" className="text-link" onClick={openCreator}>
                    Start playing <ArrowRight />
                  </button>
                </div>
              )}
              {(cloudDashboard.error || rejoinCloudRoom.error || removeCloudRoom.error) && (
                <div className="inline-error dashboard-data-error">
                  {
                    (cloudDashboard.error ?? rejoinCloudRoom.error ?? removeCloudRoom.error)
                      ?.message
                  }
                </div>
              )}
            </section>
            <section className="dashboard-support-strip" aria-labelledby="dashboard-support-title">
              <div>
                <span className="eyebrow">HELP & IDEAS</span>
                <h2 id="dashboard-support-title">Make Topzle better with us.</h2>
                <p>Send an idea or report a problem without leaving your dashboard.</p>
              </div>
              <div>
                <button type="button" onClick={() => setSupportKind("feedback")}>
                  <MessageSquareText />
                  <span>
                    <strong>Send feedback</strong>
                    <small>Ideas, requests, and suggestions</small>
                  </span>
                </button>
                <button type="button" onClick={() => setSupportKind("issue")}>
                  <Bug />
                  <span>
                    <strong>Report issue</strong>
                    <small>Tell us what is not working</small>
                  </span>
                </button>
              </div>
            </section>
          </>
        )}

        {section === "stats" && (
          <section className="dashboard-panel">
            <div className="stat-grid">
              <article>
                <span>
                  <Gamepad2 />
                </span>
                <strong>{stats.games}</strong>
                <small>Games started</small>
              </article>
              <article>
                <span>
                  <Bot />
                </span>
                <strong>{stats.aiGames}</strong>
                <small>AI matches</small>
              </article>
              <article>
                <span>
                  <Users />
                </span>
                <strong>{stats.rooms}</strong>
                <small>Rooms on this device</small>
              </article>
              <article>
                <span>
                  <Trophy />
                </span>
                <strong>{stats.wins}</strong>
                <small>Games won</small>
              </article>
              <article>
                <span>
                  <Trophy />
                </span>
                <strong className="stat-word">{stats.favorite.replaceAll("-", " ")}</strong>
                <small>Most-played game</small>
              </article>
            </div>
            <div className="activity-card">
              <div>
                <h2>Recent games</h2>
                <span>Recorded after the room server confirms a start.</span>
              </div>
              {gameActivity.length ? (
                gameActivity.slice(0, 12).map((game) => (
                  <div className="activity-row" key={game.messageId}>
                    <span className="icon-tile blue">{game.versusAi ? <Bot /> : <Users />}</span>
                    <div>
                      <strong>{game.gameId.replaceAll("-", " ")}</strong>
                      <small>{game.versusAi ? "Played with Topzle AI" : "Played together"}</small>
                    </div>
                    <time dateTime={new Date(game.startedAt).toISOString()}>
                      {formatLocalDate(game.startedAt)}
                    </time>
                  </div>
                ))
              ) : (
                <div className="panel-empty">
                  <BarChart3 />
                  <p>Start a confirmed game and its activity will appear here.</p>
                </div>
              )}
            </div>
          </section>
        )}

        {section === "friends" && (
          <section className="dashboard-panel friends-panel">
            {!session ? (
              <div className="sign-in-panel">
                <Users />
                <span className="eyebrow">SYNC YOUR PEOPLE</span>
                <h2>Sign in to add friends across devices.</h2>
                <p>
                  Guest rooms still work. An account unlocks friend search, requests, and synced
                  relationships.
                </p>
                <Link className="button button-primary" to="/sign-in">
                  Sign in or create account
                </Link>
              </div>
            ) : (
              <>
                <form
                  className="friend-search"
                  onSubmit={(event) => {
                    event.preventDefault();
                    searchFriends.mutate();
                  }}
                >
                  <Search />
                  <input
                    value={friendSearch}
                    onChange={(event) => setFriendSearch(event.target.value.slice(0, 40))}
                    placeholder="Search by display name or username"
                  />
                  <LoadingButton
                    className="button button-primary compact"
                    type="submit"
                    loading={searchFriends.isPending}
                    loadingLabel="Searching…"
                    disabled={friendSearch.trim().length < 2}
                  >
                    Find friends
                  </LoadingButton>
                </form>
                {(searchFriends.error ||
                  sendFriendRequest.error ||
                  acceptFriendRequest.error ||
                  friends.error) && (
                  <div className="inline-error">
                    {
                      (
                        searchFriends.error ??
                        sendFriendRequest.error ??
                        acceptFriendRequest.error ??
                        friends.error
                      )?.message
                    }
                  </div>
                )}
                {searchResults.length > 0 && (
                  <div className="people-list search-people">
                    <h2>Search results</h2>
                    {searchResults.map((profile) => (
                      <div className="person-row" key={profile.id}>
                        <ProfileAvatar profile={profile} />
                        <div>
                          <strong>{profile.display_name}</strong>
                          <small>@{profile.username ?? "topzle-player"}</small>
                        </div>
                        <LoadingButton
                          className="button button-ghost compact"
                          type="button"
                          onClick={() => sendFriendRequest.mutate(profile.id)}
                          loading={
                            sendFriendRequest.isPending &&
                            sendFriendRequest.variables === profile.id
                          }
                          loadingLabel="Sending…"
                        >
                          <UserPlus /> Add
                        </LoadingButton>
                      </div>
                    ))}
                  </div>
                )}
                {friends.data?.incoming.length ? (
                  <div className="people-list">
                    <h2>Friend requests</h2>
                    {friends.data.incoming.map((request) => (
                      <div className="person-row" key={request.id}>
                        <ProfileAvatar profile={request.profile} />
                        <div>
                          <strong>{request.profile?.display_name ?? "Topzle player"}</strong>
                          <small>Wants to be friends</small>
                        </div>
                        <LoadingButton
                          className="button button-primary compact"
                          type="button"
                          onClick={() => acceptFriendRequest.mutate(request.id)}
                          loading={
                            acceptFriendRequest.isPending &&
                            acceptFriendRequest.variables === request.id
                          }
                          loadingLabel="Accepting…"
                        >
                          <Check /> Accept
                        </LoadingButton>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="people-list">
                  <h2>Your friends</h2>
                  {friends.isLoading ? (
                    <div className="panel-empty">
                      <Sparkles className="spin" />
                      <p>Loading your friends…</p>
                    </div>
                  ) : friends.data?.friends.length ? (
                    friends.data.friends.map((profile) => (
                      <div className="person-row" key={profile.id}>
                        <ProfileAvatar profile={profile} />
                        <div>
                          <strong>{profile.display_name}</strong>
                          <small>
                            {profile.status ?? `@${profile.username ?? "topzle-player"}`}
                          </small>
                        </div>
                        <button
                          className="button button-ghost compact"
                          type="button"
                          onClick={openCreator}
                        >
                          <Gamepad2 /> Start room
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="panel-empty">
                      <Users />
                      <p>No friends yet. Search above and send the first request.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {section === "notifications" && (
          <section className="dashboard-panel">
            <div className="activity-card">
              <div>
                <h2>Invites</h2>
                <span>Invite links stay attached to your recent rooms.</span>
              </div>
              {recentRooms.length ? (
                recentRooms.map((room) => (
                  <div className="activity-row" key={room.roomId}>
                    <span className="icon-tile">
                      <Gamepad2 />
                    </span>
                    <div>
                      <strong>{room.snapshot.settings.title}</strong>
                      <small>Room code {room.snapshot.code}</small>
                    </div>
                    <LoadingButton
                      className="button button-ghost compact"
                      type="button"
                      onClick={() => void shareRoom(room)}
                      loading={sharingRoom === room.roomId}
                      loadingLabel="Sharing…"
                    >
                      <Share2 /> Share invite
                    </LoadingButton>
                  </div>
                ))
              ) : (
                <div className="panel-empty">
                  <Bell />
                  <p>Create a room to generate an invite link.</p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {creatorOpen && (
        <div className="modal-backdrop">
          <button
            type="button"
            className="modal-dismiss"
            aria-label="Close room creator"
            onClick={() => !createRoom.isPending && setCreatorOpen(false)}
          />
          <form className="room-creator" onSubmit={submitRoom}>
            <div className="room-creator-head">
              <div>
                <span className="eyebrow">START YOUR ROOM</span>
                <h2>
                  {creatorEntry === "game"
                    ? `Find a ${gameNames[starterGameId]} match`
                    : "Who should be able to join?"}
                </h2>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Close room creator"
                onClick={() => setCreatorOpen(false)}
                disabled={createRoom.isPending}
              >
                <X />
              </button>
            </div>
            <div className="room-creator-scroll">
              {creatorEntry === "game" && roomVisibility === "public" && (
                <section
                  className="waiting-public-rooms waiting-public-rooms-first"
                  aria-live="polite"
                >
                  <div>
                    <strong>Players waiting for {gameNames[starterGameId]}</strong>
                    <small>Live public rooms · updated automatically</small>
                  </div>
                  {!session && !guest && !guestChallengeToken ? (
                    <p>Complete the quick guest check below to see live rooms.</p>
                  ) : publicRooms.isLoading ? (
                    <p>
                      <LoaderCircle className="spin" /> Looking for a room…
                    </p>
                  ) : publicRooms.data?.rooms.length ? (
                    publicRooms.data.rooms.map((room) => {
                      const isOwnRoom = room.hostId === session?.user.id;
                      return (
                        <article key={room.id}>
                          <span className="public-room-avatar">{room.title.slice(0, 1)}</span>
                          <div>
                            <strong>{room.title}</strong>
                            <small>
                              {isOwnRoom ? "Your room · " : ""}1 player online · waiting now
                            </small>
                          </div>
                          <LoadingButton
                            type="button"
                            className="button button-primary compact"
                            loading={
                              joinPublicRoom.isPending && joinPublicRoom.variables?.id === room.id
                            }
                            loadingLabel={isOwnRoom ? "Opening…" : "Joining…"}
                            disabled={createRoom.isPending || joinPublicRoom.isPending}
                            onClick={() => joinPublicRoom.mutate(room)}
                          >
                            {isOwnRoom ? "Open" : "Join"}
                          </LoadingButton>
                        </article>
                      );
                    })
                  ) : (
                    <p>No one is waiting right now. Create the first public room below.</p>
                  )}
                </section>
              )}
              {!session && !guest && (
                <div className="guest-room-access">
                  <strong>Continue as a guest</strong>
                  <small>
                    Guests can create, join, and play every public or private game. This quick check
                    protects room creation from automated abuse.
                  </small>
                  <TurnstileWidget
                    ref={guestChallengeRef}
                    action="guest_join"
                    onToken={setGuestChallengeToken}
                  />
                </div>
              )}
              <div className="room-visibility-picker">
                <button
                  type="button"
                  className={roomVisibility === "public" ? "active" : ""}
                  aria-pressed={roomVisibility === "public"}
                  onClick={() => setRoomVisibility("public")}
                  disabled={createRoom.isPending}
                >
                  <Users />
                  <span>
                    <strong>Public room</strong>
                    <small>Meet a player who is online and waiting for this game.</small>
                  </span>
                  {roomVisibility === "public" && <Check />}
                </button>
                <button
                  type="button"
                  className={roomVisibility === "private" ? "active" : ""}
                  aria-pressed={roomVisibility === "private"}
                  onClick={() => setRoomVisibility("private")}
                  disabled={createRoom.isPending}
                >
                  <Share2 />
                  <span>
                    <strong>Private room</strong>
                    <small>Invite friends yourself with email, room code, or a share link.</small>
                  </span>
                  {roomVisibility === "private" && <Check />}
                </button>
              </div>
              <label>
                Room name
                <input
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value.slice(0, 80));
                    setTitleCustomized(true);
                  }}
                  required
                  disabled={createRoom.isPending}
                />
              </label>
              {creatorEntry === "general" ? (
                <div className="starter-settings">
                  <span>Choose a game</span>
                  <div className="starter-game-grid">
                    {roomGameCatalog.map((game) => (
                      <button
                        type="button"
                        className={starterGameId === game.id ? "active" : ""}
                        aria-pressed={starterGameId === game.id}
                        onClick={() => {
                          setStarterGameId(game.id);
                          if (!titleCustomized)
                            setTitle(
                              funRoomName(
                                session
                                  ? getUserDisplayName(session.user)
                                  : (guest?.displayName ?? "Player"),
                                game.id,
                              ),
                            );
                        }}
                        disabled={createRoom.isPending}
                        key={game.id}
                      >
                        <b>{starterGameSymbols[game.id]}</b>
                        <span>{game.name}</span>
                      </button>
                    ))}
                  </div>
                  <small>
                    The room opens on this game. Time control, AI difficulty, and who plays are
                    chosen inside the room.
                  </small>
                </div>
              ) : (
                <div className="selected-starter-game">
                  <b>{starterGameSymbols[starterGameId]}</b>
                  <span>
                    <strong>{gameNames[starterGameId]}</strong>
                    <small>
                      Time control, bot play and difficulty are selected inside the room.
                    </small>
                  </span>
                </div>
              )}
              {creatorEntry === "general" && roomVisibility === "public" && (
                <section className="waiting-public-rooms" aria-live="polite">
                  <div>
                    <strong>Players waiting for {gameNames[starterGameId]}</strong>
                    <small>Updated automatically</small>
                  </div>
                  {!session && !guest && !guestChallengeToken ? (
                    <p>Complete the quick guest check above to see live rooms.</p>
                  ) : publicRooms.isLoading ? (
                    <p>
                      <LoaderCircle className="spin" /> Looking for a room…
                    </p>
                  ) : publicRooms.data?.rooms.length ? (
                    publicRooms.data.rooms.map((room) => (
                      <article key={room.id}>
                        <span className="public-room-avatar">{room.title.slice(0, 1)}</span>
                        <div>
                          <strong>{room.title}</strong>
                          <small>1 player online · waiting now</small>
                        </div>
                        <LoadingButton
                          type="button"
                          className="button button-primary compact"
                          loading={
                            joinPublicRoom.isPending && joinPublicRoom.variables?.id === room.id
                          }
                          loadingLabel="Joining…"
                          disabled={createRoom.isPending || joinPublicRoom.isPending}
                          onClick={() => joinPublicRoom.mutate(room)}
                        >
                          Join
                        </LoadingButton>
                      </article>
                    ))
                  ) : (
                    <p>No one is waiting right now. You can create the first public room.</p>
                  )}
                </section>
              )}
              {roomVisibility === "private" && (
                <label>
                  Friend’s email or phone <small>(optional)</small>
                  <input
                    type="text"
                    autoComplete="off"
                    value={inviteContact}
                    onChange={(event) => setInviteContact(event.target.value.slice(0, 254))}
                    placeholder="friend@example.com or +91…"
                    disabled={createRoom.isPending}
                  />
                  <small>
                    Email can be sent directly. For a phone number, Topzle prepares the private link
                    for you to share through your preferred app.
                  </small>
                </label>
              )}
              {(createRoom.error || joinPublicRoom.error || publicRooms.error) && (
                <RecoverableError
                  error={createRoom.error ?? joinPublicRoom.error ?? publicRooms.error}
                  returnTo="/app"
                  onContinueAsGuest={() => {
                    clearGuest();
                    setGuestChallengeToken(null);
                    createRoom.reset();
                    joinPublicRoom.reset();
                    guestChallengeRef.current?.reset();
                  }}
                  onRetry={() => {
                    if (createRoom.error && createRoom.variables)
                      createRoom.mutate(createRoom.variables);
                    else if (joinPublicRoom.error && joinPublicRoom.variables)
                      joinPublicRoom.mutate(joinPublicRoom.variables);
                    else void publicRooms.refetch();
                  }}
                />
              )}
            </div>
            <div className="room-creator-actions">
              <LoadingButton
                type="submit"
                className="button button-primary wide"
                loading={createRoom.isPending}
                loadingLabel="Creating your room…"
                disabled={
                  !title.trim() ||
                  roomVisibility === null ||
                  joinPublicRoom.isPending ||
                  (!session && !guest && !guestChallengeToken)
                }
              >
                <Plus />{" "}
                {roomVisibility === "public" ? "Create new public room" : "Create private room"}
              </LoadingButton>
            </div>
          </form>
        </div>
      )}
      {supportKind && (
        <SupportDialog
          kind={supportKind}
          context={{ source: "dashboard" }}
          onClose={() => setSupportKind(null)}
        />
      )}
    </section>
  );
}

function ProfileAvatar({ profile }: { profile: DirectoryProfile | null }) {
  return profile?.avatar_url ? (
    <img className="person-avatar" src={profile.avatar_url} alt="" />
  ) : (
    <span className="person-avatar fallback">
      {profile?.display_name.slice(0, 1).toUpperCase() ?? "Z"}
    </span>
  );
}
